// The Perform shell (P2.3) — UX §3.1's screen layout, as regions.
//
// Structure only: where things sit, never what they are. The waveform lands at P2.6 and the
// transport at P2.7, each into a slot that already exists, so neither prompt has to renegotiate
// the layout to add its own piece. **Deck B lands at P3.7** into the column P2.3's CSS had already
// left for it — `.perform__deck--b` and `--deck-b` were written then, and the only change here is
// a third grid track and a second slot.
//
// The library drawer (UX §3.1's fourth row) is absent on purpose — FR-34 is M9, and a region
// reserved four milestones early is a region that gets redesigned before anything fills it.

import type { DragEvent, JSX, ReactNode } from 'react';

import type { DeckId } from '../platform/types/control';

export interface PerformLayoutProps {
  /** Right of the brand in the top bar: status, settings, and M8's REC chip. */
  readonly topBar?: ReactNode;
  /** The waveform lane. P2.6 fills it; until then it states its own emptiness. */
  readonly waveforms?: ReactNode;
  /** Deck A's column, on the left. */
  readonly deckA: ReactNode;
  /**
   * Deck B's column, on the right (P3.7).
   *
   * Optional, and `/bench` is why: it drives two decks with no Perform shell at all, and a required
   * slot would make a single-deck surface impossible to express. The app always passes it.
   */
  readonly deckB?: ReactNode;
  /**
   * Between the decks: the mixer at M4, pads at M6.
   *
   * **Between**, not beside — which is where it was until P3.7 only because there was nothing on its
   * right. FR-14's crossfader belongs between the two things it fades between, and putting the
   * placeholder there now means the mixer arrives into the space it will occupy rather than pushing
   * deck B sideways on the day it lands.
   */
  readonly stage?: ReactNode;
}

export function PerformLayout({
  topBar,
  waveforms,
  deckA,
  deckB,
  stage,
}: PerformLayoutProps): JSX.Element {
  return (
    <div className="perform">
      <header className="perform__topbar glass">
        <div className="perform__brand">
          <h1>SYNAPSE</h1>
        </div>
        <div className="perform__spacer" />
        {topBar}
      </header>

      <section className="perform__waveforms glass" aria-label="Waveforms">
        {waveforms ?? <div className="perform__placeholder">Waveform — P2.6</div>}
      </section>

      <div className="perform__decks">
        {deckA}
        <section className="perform__stage glass" aria-label="Mixer">
          {stage ?? <span className="perform__placeholder">Mixer — M4</span>}
        </section>
        {deckB}
      </div>
    </div>
  );
}

export interface DeckPanelProps {
  readonly deckId: DeckId;
  readonly children: ReactNode;
  /**
   * True while a file is dragged over this deck (P2.5).
   *
   * A boolean rather than a class name, so the *decision* stays in the loader and the *appearance*
   * stays in CSS — UX §5.1's rule that identity is applied by the deck, never chosen by a control
   * inside it. The outline it draws is `--deck-a` or `--deck-b` accordingly.
   */
  readonly dropActive?: boolean;
  readonly onDragOver?: (event: DragEvent<HTMLElement>) => void;
  readonly onDragLeave?: (event: DragEvent<HTMLElement>) => void;
  readonly onDrop?: (event: DragEvent<HTMLElement>) => void;
}

/**
 * One deck's column.
 *
 * Identity comes from `deckId` and is applied by a modifier class, so the deck's colour is decided
 * by *which deck it is* rather than by each control inside it choosing one — UX §5.1's rule that
 * identity applies "consistently to each deck's waveform, jog ring, channel strip highlights, drop
 * outlines, and fader caps". **Deck B renders through this same component since P3.7**, and needed
 * nothing added to it — the modifier class, the drop outline's colour and the title tint were all
 * already written per deck.
 *
 * The drag handlers sit here rather than on a wrapper inside, because UX §5.3 is specific that
 * "the whole deck becomes a drop target" — a region *within* the panel would leave a dead border
 * where a user aiming at the deck would reasonably let go.
 */
export function DeckPanel({
  deckId,
  children,
  dropActive = false,
  onDragOver,
  onDragLeave,
  onDrop,
}: DeckPanelProps): JSX.Element {
  return (
    <section
      className={
        `perform__deck perform__deck--${deckId} glass` +
        (dropActive ? ' perform__deck--drop' : '')
      }
      aria-label={`Deck ${deckId.toUpperCase()}`}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <div className="perform__deck-title">Deck {deckId.toUpperCase()}</div>
      {children}
    </section>
  );
}
